import React, { useState, useMemo } from 'react';
import { Popover, message, Input } from 'antd';
import { useTheme } from '../../contexts/useTheme';
import { getWeatherIconFileName } from '../../utils/weatherIconFileMap';

interface WeatherIconPickerProps {
  currentCode: string | null;
  onSelect: (code: string) => Promise<void>;
  codeType: 'daily' | 'hourly';
  children: React.ReactNode;
}

interface WeatherOption {
  code4: string;
  code5: string;
  label: string;
  icon: string;
  category: string;
}

const CATEGORIES = [
  {
    name: 'Clear / Cloudy',
    codes: [
      { code4: '1000', label: 'Clear, Sunny' },
      { code4: '1100', label: 'Mostly Clear' },
      { code4: '1101', label: 'Partly Cloudy' },
      { code4: '1102', label: 'Mostly Cloudy' },
      { code4: '1001', label: 'Cloudy' },
    ],
  },
  {
    name: 'Fog',
    codes: [
      { code4: '2100', label: 'Light Fog' },
      { code4: '2000', label: 'Fog' },
    ],
  },
  {
    name: 'Rain',
    codes: [
      { code4: '4000', label: 'Drizzle' },
      { code4: '4200', label: 'Light Rain' },
      { code4: '4001', label: 'Rain' },
      { code4: '4201', label: 'Heavy Rain' },
    ],
  },
  {
    name: 'Snow',
    codes: [
      { code4: '5001', label: 'Flurries' },
      { code4: '5100', label: 'Light Snow' },
      { code4: '5000', label: 'Snow' },
      { code4: '5101', label: 'Heavy Snow' },
    ],
  },
  {
    name: 'Freezing',
    codes: [
      { code4: '6000', label: 'Freezing Drizzle' },
      { code4: '6200', label: 'Light Freezing Rain' },
      { code4: '6001', label: 'Freezing Rain' },
      { code4: '6201', label: 'Heavy Freezing Rain' },
    ],
  },
  {
    name: 'Ice / Severe',
    codes: [
      { code4: '7102', label: 'Light Ice Pellets' },
      { code4: '7000', label: 'Ice Pellets' },
      { code4: '7101', label: 'Heavy Ice Pellets' },
      { code4: '8000', label: 'Thunderstorm' },
    ],
  },
];

function buildOptions(): WeatherOption[] {
  const options: WeatherOption[] = [];
  for (const cat of CATEGORIES) {
    for (const item of cat.codes) {
      const code5 = item.code4 + '0';
      options.push({
        code4: item.code4,
        code5,
        label: item.label,
        icon: `/weather-icons/${getWeatherIconFileName(code5)}`,
        category: cat.name,
      });
    }
  }
  return options;
}

const ALL_OPTIONS = buildOptions();

export const WeatherIconPicker: React.FC<WeatherIconPickerProps> = ({
  currentCode,
  onSelect,
  codeType,
  children,
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const normalizedCurrent = useMemo(() => {
    if (!currentCode) return null;
    if (currentCode.length === 5) return currentCode.slice(0, 4);
    return currentCode;
  }, [currentCode]);

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return CATEGORIES;
    const term = search.toLowerCase();
    return CATEGORIES
      .map(cat => ({
        ...cat,
        codes: cat.codes.filter(c => c.label.toLowerCase().includes(term)),
      }))
      .filter(cat => cat.codes.length > 0);
  }, [search]);

  const handleSelect = async (option: WeatherOption) => {
    const codeToSave = codeType === 'daily' ? option.code5 : option.code4;
    if (codeToSave === currentCode) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      await onSelect(codeToSave);
      message.success(`Icon updated to ${option.label}`);
      setOpen(false);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to update icon';
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <div style={{ width: 320, maxHeight: 400, overflow: 'auto' }}>
      <Input
        placeholder="Search weather..."
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        style={{ marginBottom: 8 }}
        autoFocus
      />
      {filteredCategories.map(cat => (
        <div key={cat.name} style={{ marginBottom: 8 }}>
          <div style={{
            fontSize: 11,
            fontWeight: 600,
            color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)',
            marginBottom: 4,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}>
            {cat.name}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {cat.codes.map(item => {
              const code5 = item.code4 + '0';
              const iconPath = `/weather-icons/${getWeatherIconFileName(code5)}`;
              const isSelected = normalizedCurrent === item.code4;
              return (
                <div
                  key={item.code4}
                  onClick={() => {
                    if (!saving) {
                      const opt = ALL_OPTIONS.find(o => o.code4 === item.code4)!;
                      handleSelect(opt);
                    }
                  }}
                  title={item.label}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '4px 6px',
                    borderRadius: 6,
                    cursor: saving ? 'wait' : 'pointer',
                    border: isSelected
                      ? `2px solid ${isDark ? '#1890ff' : '#1677ff'}`
                      : '2px solid transparent',
                    backgroundColor: isSelected
                      ? (isDark ? 'rgba(24, 144, 255, 0.1)' : 'rgba(22, 119, 255, 0.06)')
                      : 'transparent',
                    transition: 'all 0.15s',
                    width: 58,
                    opacity: saving ? 0.5 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = isDark
                        ? 'rgba(255,255,255,0.08)'
                        : 'rgba(0,0,0,0.04)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <img
                    src={iconPath}
                    alt={item.label}
                    style={{ width: 32, height: 32, objectFit: 'contain' }}
                  />
                  <span style={{
                    fontSize: 9,
                    textAlign: 'center',
                    lineHeight: 1.2,
                    marginTop: 2,
                    color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.65)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}>
                    {item.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {filteredCategories.length === 0 && (
        <div style={{ padding: 12, textAlign: 'center', color: isDark ? '#999' : '#666', fontSize: 12 }}>
          No matching weather conditions
        </div>
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={(visible) => {
        if (!saving) {
          setOpen(visible);
          if (!visible) setSearch('');
        }
      }}
      placement="bottom"
    >
      <span
        style={{
          cursor: 'pointer',
          display: 'inline-block',
          borderRadius: 6,
          border: `1px dashed ${isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)'}`,
          padding: 2,
          transition: 'border-color 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.35)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)';
        }}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        {children}
      </span>
    </Popover>
  );
};
